/**
 * Gestionnaire de sessions Cloud avec Supabase Realtime
 * Remplace Socket.io pour une compatibilité totale avec Netlify et autres plateformes serverless
 */

import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClientDirect } from '@/lib/supabaseClient';
import {
  SessionInfo,
  SessionType,
  CreateSessionConfig,
  JoinSessionConfig,
  SessionEventCallbacks,
  SessionStats,
  SyncMessage,
  SessionError,
  SessionStatus
} from './types';

// Types pour les tables Supabase
interface SupabaseSession {
  id: string;
  code: string;
  type: 'cloud' | 'local';
  status: 'active' | 'inactive' | 'expired';
  server_device_id: string | null;
  config: any;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

interface SupabaseDevice {
  id: string;
  session_id: string;
  device_id: string;
  name: string;
  type: 'mobile' | 'desktop' | 'tablet';
  user_agent: string;
  role: 'server' | 'remote';
  joined_at: string;
  last_seen: string;
}

interface SupabaseMessage {
  id: string;
  session_id: string;
  from_device_id: string;
  message_type: string;
  payload: any;
  created_at: string;
}

/**
 * Gestionnaire de sessions Cloud utilisant Supabase Realtime
 */
export class SupabaseSessionManager {
  private supabase: SupabaseClient;
  private sessionInfo: SessionInfo | null = null;
  private callbacks: SessionEventCallbacks = {};
  private stats: SessionStats = {
    totalMessages: 0,
    averageLatency: 0,
    dataTransferred: 0,
    uptime: 0
  };
  
  private channel: RealtimeChannel | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private connectionStartTime: number = 0;
  private deviceInfo: { name: string; type: string } = { name: 'Appareil', type: 'desktop' };

  constructor() {
    this.supabase = getSupabaseClientDirect();
  }

  /**
   * Génère un code de session unique à 6 caractères
   */
  private generateSessionCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  /**
   * Génère un ID d'appareil unique
   */
  private generateDeviceId(): string {
    return `d-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
  }

  /**
   * Crée une nouvelle session cloud
   */
  async createSession(config: CreateSessionConfig): Promise<SessionInfo> {
    try {
      // Récupérer l'utilisateur connecté
      const { data: { user } } = await this.supabase.auth.getUser();
      
      // Générer un code unique
      let code = this.generateSessionCode();
      let attempts = 0;
      const maxAttempts = 5;
      
      // Vérifier que le code n'existe pas déjà
      while (attempts < maxAttempts) {
        const { data: existing } = await this.supabase
          .from('sessions')
          .select('id')
          .eq('code', code)
          .eq('status', 'active')
          .single();
        
        if (!existing) break;
        code = this.generateSessionCode();
        attempts++;
      }

      const deviceId = this.generateDeviceId();
      const sessionId = crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).substring(2)}`;

      // Stocker les infos de l'appareil
      this.deviceInfo = {
        name: config.deviceInfo?.name || 'PC',
        type: config.deviceInfo?.type || 'desktop'
      };

      // Créer la session dans Supabase
      const { data: session, error: sessionError } = await this.supabase
        .from('sessions')
        .insert({
          id: sessionId,
          code,
          type: 'cloud',
          status: 'active',
          server_device_id: deviceId,
          config: {},
          created_by: user?.id || null,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        })
        .select()
        .single();

      if (sessionError) {
        throw new SessionError(`Erreur création session: ${sessionError.message}`, 'NETWORK_ERROR');
      }

      // Ajouter l'appareil créateur (server)
      const { error: deviceError } = await this.supabase
        .from('session_devices')
        .insert({
          session_id: sessionId,
          device_id: deviceId,
          name: config.deviceInfo?.name || 'Appareil',
          type: config.deviceInfo?.type || 'desktop',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          role: 'server'
        });

      if (deviceError) {
        console.error('Erreur ajout appareil:', deviceError);
      }

      // Construire l'info de session
      this.sessionInfo = {
        id: sessionId,
        code,
        type: 'cloud',
        status: 'active',
        deviceId,
        role: 'server',
        joinUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/grid?join=${code}`
      };

      // S'abonner au canal Realtime
      await this.subscribeToChannel(sessionId, deviceId);

      console.log(`☁️ [SUPABASE_SESSION] Session créée: ${code}`);
      return this.sessionInfo;

    } catch (error: any) {
      console.error('❌ [SUPABASE_SESSION] Erreur création:', error);
      throw error instanceof SessionError ? error : 
        new SessionError('Impossible de créer la session cloud', 'NETWORK_ERROR');
    }
  }

  /**
   * Rejoint une session existante par son code
   */
  async joinSession(config: JoinSessionConfig): Promise<SessionInfo> {
    try {
      // Rechercher la session par son code
      const { data: session, error: sessionError } = await this.supabase
        .from('sessions')
        .select('*')
        .eq('code', config.sessionCode.toUpperCase())
        .eq('status', 'active')
        .single();

      if (sessionError || !session) {
        throw new SessionError('Session introuvable ou expirée', 'SESSION_NOT_FOUND');
      }

      // Vérifier si la session n'est pas expirée
      if (new Date(session.expires_at) < new Date()) {
        throw new SessionError('Session expirée', 'SESSION_EXPIRED');
      }

      const deviceId = this.generateDeviceId();

      // Stocker les infos de l'appareil
      this.deviceInfo = {
        name: config.deviceInfo?.name || 'Mobile',
        type: config.deviceInfo?.type || 'mobile'
      };

      // Ajouter l'appareil à la session
      const { error: deviceError } = await this.supabase
        .from('session_devices')
        .insert({
          session_id: session.id,
          device_id: deviceId,
          name: config.deviceInfo?.name || 'Appareil',
          type: config.deviceInfo?.type || 'mobile',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          role: 'remote'
        });

      if (deviceError) {
        console.error('Erreur ajout appareil:', deviceError);
      }

      // Construire l'info de session
      this.sessionInfo = {
        id: session.id,
        code: session.code,
        type: 'cloud',
        status: 'active',
        deviceId,
        role: 'remote'
      };

      // S'abonner au canal Realtime (la Presence notifie automatiquement les autres)
      await this.subscribeToChannel(session.id, deviceId);

      console.log(`🔗 [SUPABASE_SESSION] Rejoint session: ${session.code}`);
      return this.sessionInfo;

    } catch (error: any) {
      console.error('❌ [SUPABASE_SESSION] Erreur connexion:', error);
      throw error instanceof SessionError ? error : 
        new SessionError('Impossible de rejoindre la session', 'NETWORK_ERROR');
    }
  }

  /**
   * S'abonne au canal Realtime pour cette session
   */
  private async subscribeToChannel(sessionId: string, deviceId: string): Promise<void> {
    // Créer un canal pour cette session
    this.channel = this.supabase.channel(`session:${sessionId}`, {
      config: {
        broadcast: { self: false }, // Ne pas recevoir ses propres messages
        presence: { key: deviceId }
      }
    });

    // Écouter les messages broadcast
    this.channel.on('broadcast', { event: 'sync' }, (payload) => {
      this.handleIncomingMessage(payload.payload);
    });

    // Écouter les changements de présence (appareils qui rejoignent/quittent)
    this.channel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
      // Ignorer notre propre appareil
      if (key === deviceId) {
        console.log(`👋 [SUPABASE_SESSION] Notre appareil connecté (ignoré):`, key);
        return;
      }
      console.log(`👋 [SUPABASE_SESSION] Appareil distant rejoint:`, key);
      newPresences.forEach((presence: any) => {
        this.emit('onDeviceJoin', {
          id: key,
          deviceId: key,
          name: presence.name || 'Appareil',
          type: presence.type || 'desktop'
        });
      });
    });

    this.channel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
      // Ignorer notre propre appareil
      if (key === deviceId) {
        console.log(`👋 [SUPABASE_SESSION] Notre appareil déconnecté (ignoré):`, key);
        return;
      }
      console.log(`👋 [SUPABASE_SESSION] Appareil distant parti:`, key);
      this.emit('onDeviceLeave', key);
    });

    // S'abonner au canal
    await this.channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`🔌 [SUPABASE_SESSION] Canal connecté`);
        this.connectionStartTime = Date.now();
        
        // Tracker la présence avec les infos de l'appareil
        await this.channel?.track({
          deviceId,
          name: this.deviceInfo.name,
          type: this.deviceInfo.type,
          role: this.sessionInfo?.role || 'remote',
          online_at: new Date().toISOString()
        });

        this.startHeartbeat();
        
        // Gérer la fermeture de page
        this.setupPageUnloadHandler();
      } else if (status === 'CHANNEL_ERROR') {
        console.error('❌ [SUPABASE_SESSION] Erreur canal');
        this.emit('onConnectionLost');
      }
    });
  }

  /**
   * Traite les messages entrants
   */
  private handleIncomingMessage(message: any): void {
    // Ne pas traiter nos propres messages
    if (message.fromDeviceId === this.sessionInfo?.deviceId) {
      return;
    }

    console.log(`📨 [SUPABASE_SESSION] Message reçu:`, message.type);

    switch (message.type) {
      case 'CONFIG_UPDATE':
      case 'CONFIG_SYNC':
        this.emit('onConfigUpdate', message.data);
        break;
      
      case 'ANIMATION_STATE':
        this.emit('onAnimationStateUpdate', message.data?.isAnimating);
        break;
      
      case 'DEVICE_JOINED':
        // Ignoré - géré par Supabase Presence (plus fiable)
        break;
      
      case 'DEVICE_LEFT':
        // Ignoré - géré par Supabase Presence (plus fiable)
        break;
      
      case 'SESSION_CLOSE':
        console.log(`📨 [SUPABASE_SESSION] Session fermée par le serveur`);
        // Nettoyer notre état local
        this.stopHeartbeat();
        if (this.channel) {
          this.channel.unsubscribe();
          this.channel = null;
        }
        this.sessionInfo = null;
        // Notifier l'UI
        this.emit('onSessionTerminated');
        break;
      
      default:
        // Traiter comme une mise à jour de config générique
        this.emit('onConfigUpdate', { [message.type]: message.data });
        break;
    }

    this.updateStats(undefined, JSON.stringify(message).length);
  }

  /**
   * Envoie un message de synchronisation
   */
  async syncConfig(config: any): Promise<void> {
    await this.sendMessage('CONFIG_UPDATE', config);
  }

  /**
   * Envoie un message personnalisé
   */
  async sendSyncMessage(type: string, data: any): Promise<void> {
    await this.sendMessage(type, data);
  }

  /**
   * Envoie un message via le canal Realtime
   */
  private async sendMessage(type: string, data: any): Promise<void> {
    if (!this.channel || !this.sessionInfo) {
      throw new SessionError('Session non connectée', 'NETWORK_ERROR');
    }

    const message = {
      type,
      data,
      fromDeviceId: this.sessionInfo.deviceId,
      sessionId: this.sessionInfo.id,
      timestamp: Date.now()
    };

    // Envoyer via broadcast (temps réel, pas de persistance)
    await this.channel.send({
      type: 'broadcast',
      event: 'sync',
      payload: message
    });

    this.updateStats(undefined, JSON.stringify(message).length);
  }

  /**
   * Démarre le heartbeat pour maintenir la présence
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(async () => {
      if (this.channel && this.sessionInfo) {
        // Mettre à jour la présence avec les mêmes infos que le track initial
        // pour éviter que Supabase ne génère un leave+join avec des données différentes
        await this.channel.track({
          deviceId: this.sessionInfo.deviceId,
          name: this.deviceInfo.name,
          type: this.deviceInfo.type,
          role: this.sessionInfo.role || 'remote',
          online_at: new Date().toISOString()
        });

        // Mettre à jour last_seen dans la base
        try {
          await this.supabase
            .from('session_devices')
            .update({ last_seen: new Date().toISOString() })
            .eq('session_id', this.sessionInfo.id)
            .eq('device_id', this.sessionInfo.deviceId);
        } catch {
          // Ignorer les erreurs de heartbeat DB (réseau instable)
        }
      }
    }, 30000);
  }

  /**
   * Arrête le heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Configure le handler de fermeture de page
   */
  private setupPageUnloadHandler(): void {
    if (typeof window === 'undefined') return;
    
    if ((window as any)._supabaseSessionUnloadHandler) {
      return;
    }

    const unloadHandler = async () => {
      console.log('🚪 [SUPABASE_SESSION] Page fermée, déconnexion...');
      
      if (this.sessionInfo) {
        // Notifier les autres appareils
        try {
          await this.sendMessage('DEVICE_LEFT', {
            deviceId: this.sessionInfo.deviceId
          });
        } catch (e) {
          // Ignorer les erreurs lors de la fermeture
        }
      }
      
      this.channel?.unsubscribe();
    };

    window.addEventListener('beforeunload', unloadHandler);
    window.addEventListener('pagehide', unloadHandler);
    (window as any)._supabaseSessionUnloadHandler = unloadHandler;
  }

  /**
   * Nettoie le handler de fermeture de page
   */
  private cleanupPageUnloadHandler(): void {
    if (typeof window === 'undefined') return;
    
    const handler = (window as any)._supabaseSessionUnloadHandler;
    if (handler) {
      window.removeEventListener('beforeunload', handler);
      window.removeEventListener('pagehide', handler);
      delete (window as any)._supabaseSessionUnloadHandler;
    }
  }

  /**
   * Déconnecte de la session
   */
  async disconnect(): Promise<void> {
    try {
      this.stopHeartbeat();
      this.cleanupPageUnloadHandler();

      // Sauvegarder les infos avant de les effacer
      const sessionId = this.sessionInfo?.id;
      const deviceId = this.sessionInfo?.deviceId;
      const isServer = this.sessionInfo?.role === 'server';

      // Se désabonner du canal en premier (pour arrêter de recevoir des messages)
      if (this.channel) {
        try {
          await this.channel.unsubscribe();
        } catch (e) {
          // console.warn('⚠️ [SUPABASE_SESSION] Erreur désabonnement canal:', e);
        }
        this.channel = null;
      }

      if (sessionId && deviceId) {
        // Supprimer notre appareil de la session
        try {
          await this.supabase
            .from('session_devices')
            .delete()
            .eq('session_id', sessionId)
            .eq('device_id', deviceId);
        } catch (e) {
          // console.warn('⚠️ [SUPABASE_SESSION] Erreur suppression appareil:', e);
        }

        // Si on est le serveur, marquer la session comme inactive
        if (isServer) {
          try {
            const { error } = await this.supabase
              .from('sessions')
              .update({ status: 'inactive' })
              .eq('id', sessionId);
            
            if (error) {
              // console.warn('⚠️ [SUPABASE_SESSION] Impossible de fermer la session (RLS):', error.message);
            }
          } catch (e) {
            // console.warn('⚠️ [SUPABASE_SESSION] Erreur fermeture session:', e);
          }
        }
      }

      // Réinitialiser l'état
      this.sessionInfo = null;
      // console.log(`🔚 [SUPABASE_SESSION] Déconnecté`);

      // Émettre l'événement de fin de session
      this.emit('onSessionTerminated');

    } catch (error) {
      // console.error('❌ [SUPABASE_SESSION] Erreur déconnexion:', error);
      // Réinitialiser quand même l'état pour éviter un état incohérent
      this.sessionInfo = null;
      this.channel = null;
    }
  }

  // ============================================
  // Méthodes utilitaires
  // ============================================

  public getSessionInfo(): SessionInfo | null {
    return this.sessionInfo;
  }

  public getStats(): SessionStats {
    return { ...this.stats };
  }

  public setCallbacks(callbacks: SessionEventCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  public isConnected(): boolean {
    return this.sessionInfo?.status === 'active';
  }

  public getSessionCode(): string | null {
    return this.sessionInfo?.code || null;
  }

  public getConnectionStatus(): SessionStatus {
    if (!this.sessionInfo) return 'inactive';
    return this.sessionInfo.status;
  }

  private updateStats(latency?: number, dataSize?: number): void {
    this.stats.totalMessages++;
    
    if (latency !== undefined) {
      this.stats.averageLatency = 
        (this.stats.averageLatency * (this.stats.totalMessages - 1) + latency) / 
        this.stats.totalMessages;
    }
    
    if (dataSize !== undefined) {
      this.stats.dataTransferred += dataSize;
    }
  }

  private emit(event: keyof SessionEventCallbacks, ...args: any[]): void {
    const callback = this.callbacks[event];
    if (callback) {
      try {
        (callback as any)(...args);
      } catch (error) {
        console.error(`❌ [SUPABASE_SESSION] Erreur callback ${event}:`, error);
      }
    }
  }
}

// Export par défaut pour compatibilité
export default SupabaseSessionManager;
